import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Banknote, Sparkles } from "lucide-react";

const SESSION_KEY = "veloskin.affiliate.bank_prompt.dismissed";
const TOUR_KEY_PREFIX = "velo_tour_done_";

/**
 * A message-only nudge shown to affiliates on every fresh login until they
 * add their bank account (via the "Bank on file" card on the dashboard).
 *
 * • Does NOT collect any bank fields itself — sends the user to the existing
 *   "Bank on file" section on their dashboard via the `onGoToBank` callback,
 *   which scrolls to and opens that section's dialog.
 * • "Remind me later" persists a dismiss flag in sessionStorage so the
 *   prompt reappears on the next login.
 * • Waits for the first-login tour to finish before opening (otherwise Radix
 *   Dialog's pointer-events:none locks the tour buttons).
 * • Never shown to customers.
 */
export default function AffiliateBankPromptModal({ user, onGoToBank }) {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!user || user.role !== "affiliate") return undefined;
    if (user.bank_details) return undefined;
    try {
      if (sessionStorage.getItem(SESSION_KEY) === "1") return undefined;
    } catch { /* sessionStorage may be blocked */ }

    const tourKey = user.id ? `${TOUR_KEY_PREFIX}${user.id}` : null;
    const tryOpen = () => {
      if (!tourKey || localStorage.getItem(tourKey)) {
        setOpen(true);
        return true;
      }
      return false;
    };
    if (tryOpen()) return undefined;
    const iv = setInterval(() => { if (tryOpen()) clearInterval(iv); }, 800);
    return () => clearInterval(iv);
  }, [user]);

  const dismiss = () => {
    try { sessionStorage.setItem(SESSION_KEY, "1"); } catch { /* ignore */ }
    setOpen(false);
  };

  const goToBank = () => {
    dismiss();
    onGoToBank?.();
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) dismiss(); else setOpen(true); }}>
      <DialogContent
        data-testid="affiliate-bank-prompt-dialog"
        className="bg-[#22103F] border-white/10 text-white max-w-md"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Banknote className="w-5 h-5 text-pink-400" strokeWidth={1.75} />
            One quick step to get paid
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-1">
          <div className="velo-surface p-3.5 rounded-lg flex items-start gap-2.5 text-sm text-slate-200">
            <Sparkles className="w-4 h-4 text-pink-400 mt-0.5 shrink-0" strokeWidth={2} />
            <div>
              As an affiliate you can convert your points into cash payouts. Please tap <span className="text-pink-300 font-medium">"Add bank account details"</span> on your dashboard — every future payout goes straight to that account.
            </div>
          </div>
          <div className="text-xs text-slate-400 leading-relaxed">
            This reminder will keep appearing every login until your bank details are saved.
          </div>
        </div>
        <DialogFooter className="flex-col sm:flex-row gap-2">
          <button
            type="button"
            data-testid="btn-affiliate-bank-later"
            onClick={dismiss}
            className="text-xs text-slate-400 hover:text-slate-200 underline underline-offset-2 order-2 sm:order-1"
          >
            Remind me later
          </button>
          <Button
            data-testid="btn-affiliate-bank-go"
            onClick={goToBank}
            className="w-full sm:w-auto rounded-full bg-pink-500 hover:bg-pink-400 text-black h-11 order-1 sm:order-2"
          >
            Take me there
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
